import React, { useMemo, useState } from 'react';
import { handoverRepBricks } from '../services/adminService';
import { formatDate } from '../utils/period';
import { apiErrorMessage } from '../utils/apiError';

// Brick devir akışı. Tipik senaryo: ayrılan bir mümessilin brick'leri geçici (dummy)
// bir territory'ye atanır, ay içinde işe başlayan yeni mümessil devralır.
// Devir ayı, atamanın kendi başlangıç ayı ile aynıysa satır yerinde güncellenir —
// dummy atama hiç yapılmamış gibi olur. Sonraki bir ay seçilirse dönem bölünür.
// Temsilci silinmez; yalnızca atamalar devredilir.
const RepHandoverModal = ({ rep, repBricks, reps, regions, tid, onClose, onDone }) => {
  const openBricks = useMemo(
    () => repBricks.filter(rb => rb.repId === rep.id && !rb.endDate),
    [repBricks, rep.id]
  );

  const [month, setMonth] = useState('');
  // Satır başına hedef temsilci; boş bırakılan satırlar devredilmez.
  const [targets, setTargets] = useState({});
  const [bulkRepId, setBulkRepId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const effectiveDate = /^\d{4}-\d{2}$/.test(month) ? `${month}-01` : '';

  // Devir hedefleri: aynı ekipteki diğer temsilciler. Ekipte başka temsilci yoksa tüm liste.
  const sameTeamReps = reps.filter(r => r.id !== rep.id && r.teamName === rep.teamName);
  const candidateReps = sameTeamReps.length > 0 ? sameTeamReps : reps.filter(r => r.id !== rep.id);
  const regionNameOf = (r) => regions.find(rg => rg.id === r.regionId)?.regionName || '-';

  const maxMonth = `${new Date().getFullYear() + 1}-12`;

  const selected = openBricks.filter(rb => targets[rb.id]);
  // Seçilen ay, atamanın başlangıç ayı ise satır yerinde güncellenir; sonrası dönem böler.
  const isInPlace = (rb) => Boolean(effectiveDate) && effectiveDate <= rb.startDate;
  const inPlaceCount = selected.filter(isInPlace).length;

  const applyBulk = () => {
    if (!bulkRepId) return;
    setTargets(Object.fromEntries(openBricks.map(rb => [rb.id, bulkRepId])));
  };

  const canSave = Boolean(effectiveDate) && selected.length > 0;

  const handleSave = async () => {
    setError('');
    if (!effectiveDate) { setError('Devir ayını seçin.'); return; }
    if (selected.length === 0) { setError('En az bir brick için yeni temsilci seçin.'); return; }
    setSaving(true);
    try {
      await handoverRepBricks(
        rep.id,
        effectiveDate,
        selected.map(rb => ({ repBrickId: rb.id, newRepId: parseInt(targets[rb.id], 10) })),
        tid
      );
      onDone(`${selected.length} brick ataması devredildi.`);
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={styles.overlay} onClick={saving ? undefined : onClose}>
      <div style={styles.modal} onClick={e => e.stopPropagation()}>
        <h3 style={styles.title}>Brick Devri — {rep.repName}</h3>

        {openBricks.length === 0 ? (
          <>
            <p style={styles.desc}>Bu temsilcinin açık brick ataması yok; devredilecek bir şey yok.</p>
            <div style={styles.actions}>
              <button style={styles.cancelButton} onClick={onClose}>Kapat</button>
            </div>
          </>
        ) : (
          <>
            <p style={styles.desc}>
              Temsilcinin <strong>{openBricks.length}</strong> açık brick ataması yeni temsilcilere
              devredilecek. Devir ayı atamanın kendi başlangıç ayı ile aynıysa atama{' '}
              <strong>yerinde güncellenir</strong> — geçici (dummy) atama hiç yapılmamış gibi olur.
              Daha sonraki bir ay seçilirse mevcut atama bir önceki ayın sonunda kapatılır ve yeni
              atama seçilen ayın 1'inde başlar. Oranlar mevcut atamalardan aynen taşınır.
              Bu işlem temsilciyi silmez.
            </p>

            <div style={styles.periodRow}>
              <div style={styles.field}>
                <label style={styles.label}>Devir Ayı *</label>
                <input
                  type="month" value={month} max={maxMonth}
                  onChange={e => setMonth(e.target.value)}
                  style={{ ...styles.input, borderColor: '#166534' }}
                />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Geçerlilik</label>
                <div style={styles.periodInfo}>
                  {effectiveDate
                    ? `${formatDate(effectiveDate)} itibarıyla · ${inPlaceCount}/${selected.length} atama yerinde güncellenecek`
                    : '— ay seçilince hesaplanır —'}
                </div>
              </div>
            </div>

            <div style={styles.bulkRow}>
              <label style={styles.label}>Tüm brick'leri şu temsilciye devret:</label>
              <select
                style={{ ...styles.input, minWidth: '240px' }}
                value={bulkRepId}
                onChange={e => setBulkRepId(e.target.value)}
              >
                <option value="">Temsilci seçin...</option>
                {candidateReps.map(r => (
                  <option key={r.id} value={r.id}>{regionNameOf(r)} → {r.repName}</option>
                ))}
              </select>
              <button
                style={{ ...styles.secondaryButton, opacity: bulkRepId ? 1 : 0.5 }}
                disabled={!bulkRepId}
                onClick={applyBulk}
              >
                Tümüne Uygula
              </button>
              <button style={styles.cancelButton} onClick={() => setTargets({})}>Seçimi Temizle</button>
              <span style={{ fontSize: '12px', color: selected.length ? '#166534' : '#64748b' }}>
                {selected.length} / {openBricks.length} brick seçili
              </span>
            </div>

            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Brick</th>
                    <th style={styles.th}>Oran %</th>
                    <th style={styles.th}>Mevcut Başlangıç</th>
                    <th style={styles.th}>Yeni Temsilci</th>
                    <th style={styles.th}>Sonuç</th>
                  </tr>
                </thead>
                <tbody>
                  {openBricks.map(rb => (
                    <tr key={rb.id}>
                      <td style={styles.td}><strong>{rb.brickName}</strong></td>
                      <td style={styles.td}>{rb.splitRatio}%</td>
                      <td style={styles.td}>{rb.startDate}</td>
                      <td style={styles.td}>
                        <select
                          style={styles.input}
                          value={targets[rb.id] || ''}
                          onChange={e => setTargets(prev => ({ ...prev, [rb.id]: e.target.value }))}
                        >
                          <option value="">— devretme —</option>
                          {candidateReps.map(r => (
                            <option key={r.id} value={r.id}>{regionNameOf(r)} → {r.repName}</option>
                          ))}
                        </select>
                      </td>
                      <td style={{ ...styles.td, fontSize: '12px' }}>
                        {!targets[rb.id] ? (
                          <span style={{ color: '#94a3b8' }}>değişmez</span>
                        ) : !effectiveDate ? (
                          <span style={{ color: '#94a3b8' }}>ay seçin</span>
                        ) : isInPlace(rb) ? (
                          <span style={{ color: '#166534' }}>
                            yerinde ({formatDate(rb.startDate)} itibarıyla)
                          </span>
                        ) : (
                          <span style={{ color: '#b45309' }}>
                            dönem bölünür ({formatDate(effectiveDate)} itibarıyla)
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {error && <p style={styles.error}>{error}</p>}

            <div style={styles.actions}>
              <button
                style={{ ...styles.primaryButton, opacity: (saving || !canSave) ? 0.6 : 1 }}
                disabled={saving || !canSave}
                onClick={handleSave}
              >
                {saving ? 'Kaydediliyor...' : 'Devret'}
              </button>
              <button style={styles.cancelButton} onClick={onClose} disabled={saving}>İptal</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
    justifyContent: 'center', alignItems: 'center', zIndex: 1000, padding: '16px',
  },
  modal: {
    backgroundColor: 'white', padding: '24px', borderRadius: '8px',
    width: '900px', maxWidth: '100%', maxHeight: '90vh', overflowY: 'auto',
    boxShadow: '0 4px 24px rgba(0,0,0,0.2)',
  },
  title: { margin: '0 0 12px', color: '#1a1a2e', fontSize: '18px' },
  desc: { fontSize: '13px', color: '#64748b', margin: '0 0 16px', lineHeight: 1.5 },
  periodRow: { display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '16px' },
  field: { display: 'flex', flexDirection: 'column', gap: '4px' },
  label: { fontSize: '12px', color: '#64748b', fontWeight: 600 },
  input: {
    padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: '6px',
    fontSize: '13px', minWidth: '180px',
  },
  periodInfo: {
    padding: '8px 10px', border: '1px solid #166534', borderRadius: '6px',
    backgroundColor: '#e0f2fe', color: '#166534', fontSize: '13px', fontWeight: 600,
    minWidth: '340px',
  },
  bulkRow: {
    display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap',
    padding: '12px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0',
    borderRadius: '6px', marginBottom: '12px',
  },
  tableWrap: { overflowX: 'auto', maxHeight: '340px', overflowY: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
  th: {
    textAlign: 'left', padding: '8px', borderBottom: '2px solid #e2e8f0',
    fontSize: '12px', color: '#64748b', position: 'sticky', top: 0, backgroundColor: 'white',
  },
  td: { padding: '8px', borderBottom: '1px solid #f1f5f9' },
  error: {
    margin: '12px 0 0', padding: '10px', borderRadius: '6px',
    backgroundColor: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', fontSize: '13px',
  },
  actions: { display: 'flex', gap: '8px', marginTop: '20px' },
  primaryButton: {
    padding: '8px 16px', backgroundColor: '#166534', color: 'white',
    border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: 600,
  },
  secondaryButton: {
    padding: '8px 14px', backgroundColor: '#1a1a2e', color: 'white',
    border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px',
  },
  cancelButton: {
    padding: '8px 16px', backgroundColor: 'white', color: '#64748b',
    border: '1px solid #e2e8f0', borderRadius: '6px', cursor: 'pointer', fontSize: '13px',
  },
};

export default RepHandoverModal;
